import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  contentChildren,
  effect,
  inject,
  output,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { TabComponent } from './tab.component';

@Component({
  selector: 'rp-tabs',
  standalone: true,
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
})
export class TabsComponent<T> {
  tabs = contentChildren<TabComponent<T>>(TabComponent);

  readonly tabChange = output<T>();
  readonly triggerDisabledTab = output<void>();

  activeTabComponent: TabComponent<T> | null = null;

  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _cdRef = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      const tabsArray = this.tabs();
      const activeTab = tabsArray.find(tab => tab.isActive());

      if (activeTab && activeTab !== this.activeTabComponent) {
        this._activateTabWithoutNavigation(activeTab);
      } else if (!this.activeTabComponent && tabsArray.length > 0) {
        this._syncActiveTabWithRoute();
      }
    });
  }

  activateTab(tab: TabComponent<T>, tabElement?: HTMLLIElement): void {
    if (tab.isDisabled()) {
      this.triggerDisabledTab.emit();

      return;
    }

    const queryParams = tab.queryParams();
    const link = tab.link();

    if (queryParams || link) {
      this._router
        .navigate(link ? [link] : [], {
          relativeTo: this._route,
          queryParams,
        })
        .then(result => {
          if (result) {
            this.tabChange.emit(tab.value());
            this.activeTabComponent = tab;
          }
        });
    } else {
      this.tabChange.emit(tab.value());
      this.activeTabComponent = tab;
    }

    if (tabElement) {
      this._scrollToTab(tabElement);
    }
  }

  private _activateTabWithoutNavigation(tab: TabComponent<T>): void {
    this.activeTabComponent = tab;
    this.tabChange.emit(tab.value());
    this._cdRef.markForCheck();
  }

  private _syncActiveTabWithRoute(): void {
    const currentUrl = this._router.url.split('?')[0];
    const tabsArray = this.tabs();
    const matchingTab = tabsArray.find(tab => {
      const tabLink = tab.link();

      return tabLink && currentUrl.includes(tabLink);
    });

    if (matchingTab) {
      this._activateTabWithoutNavigation(matchingTab);
    } else if (tabsArray.length > 0) {
      this._activateTabWithoutNavigation(tabsArray[0]);
    }
  }

  private _scrollToTab(tabElement: HTMLLIElement): void {
    tabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}
